import React, {useState, useEffect, useRef} from 'react'
import {Modal} from 'react-bootstrap'
import {Contact} from './type'
import {useVoiceRecorder} from './hooks/useVoiceRecorder'
import {useMicLevel} from './hooks/useMicLevel'
import './VoiceRecordModal.scss'
import PageLoading from '../../../routing/PageLoading'

type Props = {
  show: boolean
  contact: Contact | null
  onClose: () => void
  onSaved?: () => void
}

const VoiceRecordModal: React.FC<Props> = ({show, contact, onClose, onSaved}) => {
  const {
    isRecording,
    isPaused,
    canStop,
    isUploading,
    startRecording,
    stopRecording,
    forceStopRecording,
    saveRecording,
    discardRecording,
    uploadFile,
    hasRecordedAudio,
    recordedAudioUrl,
    resetRecordingState,
  } = useVoiceRecorder({rawContactId: contact?.id || '', autoUploadOnStop: false})
  const micLevel = useMicLevel(isRecording)

  // Tab state
  const [activeTab, setActiveTab] = useState<'record' | 'upload'>('record')
  // Upload-file state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (!file) return
    if (uploadedFileUrl) URL.revokeObjectURL(uploadedFileUrl)
    setUploadedFile(file)
    setUploadedFileUrl(URL.createObjectURL(file))
  }

  const handleRemoveUploadedFile = () => {
    if (uploadedFileUrl) URL.revokeObjectURL(uploadedFileUrl)
    setUploadedFile(null)
    setUploadedFileUrl(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Clean up upload state when switching tabs or closing modal
  const handleTabSwitch = (tab: 'record' | 'upload') => {
    setActiveTab(tab)
    handleRemoveUploadedFile()
  }

  // Timer state
  const [recordingTime, setRecordingTime] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Timer effect
  useEffect(() => {
    if (isRecording && !isPaused) {
      intervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1)
      }, 1000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isRecording, isPaused])

  // Reset timer when recording stops or modal closes
  useEffect(() => {
    if (!isRecording) {
      setRecordingTime(0)
    }
  }, [isRecording])

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Ensure cleanup when the modal is programmatically hidden by parent (show -> false)
  React.useEffect(() => {
    if (!show) {
      try {
        forceStopRecording()
      } catch (_) {}
      discardRecording()
      resetRecordingState()
      setRecordingTime(0)
      handleRemoveUploadedFile()
      setActiveTab('record')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  return (
    <Modal
      show={show}
      onHide={() => {
        try {
          forceStopRecording()
        } catch (_) {}
        discardRecording()
        resetRecordingState()
        onClose()
      }}
      centered
    >
      <Modal.Header className='border-0' closeButton>
        <Modal.Title className='p-0 m-0'>
          <h3 className='fw-bolder m-0 fs-2'>AI Voice Notes</h3>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='border-top p-9'>
        {/* Tab switcher */}
        <div className='d-flex gap-2 mb-6'>
          <button
            type='button'
            className={`btn btn-sm ${activeTab === 'record' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => handleTabSwitch('record')}
            disabled={isUploading}
          >
            <i className='bi bi-mic-fill me-1'></i>Record
          </button>
          <button
            type='button'
            className={`btn btn-sm ${activeTab === 'upload' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => handleTabSwitch('upload')}
            disabled={isUploading || isRecording}
          >
            <i className='bi bi-upload me-1'></i>Upload File
          </button>
        </div>
        <div className='voice-recorder-modal'>
          <div className='vrm-grid'>
            <aside className='vrm-contact-card'>
              <div className='section-title'>Contact Information</div>
              <div className='row align-items-center mb-3 gx-2'>
                <div className='col-4'>
                  <label className='fw-bold fs-6'>Name</label>
                </div>
                <div className='col-8 text-end'>
                  <span className='text-dark'>{contact?.fullName || '-'}</span>
                </div>
              </div>
              <div className='row align-items-center mb-3 gx-2'>
                <div className='col-4'>
                  <label className='fw-bold fs-6'>Phone</label>
                </div>
                <div className='col-8 text-end'>
                  <span className='text-dark'>{contact?.phone || '-'}</span>
                </div>
              </div>
              <div className='row align-items-center mb-0 gx-2'>
                <div className='col-4'>
                  <label className='fw-bold fs-6'>Email</label>
                </div>
                <div className='col-8 text-end'>
                  <span
                    className='text-dark text-vrm-truncate'

                  >
                    {contact?.email || '-'}
                  </span>
                </div>
              </div>
            </aside>

            <section className='vrm-recorder-card'>
              <div className='vrm-header d-flex align-items-center justify-content-between mb-5 position-relative'>
                <div className='d-flex align-items-center gap-3'>
                  {(() => {
                    const micDisabled = isRecording && !isPaused && !canStop
                    const titleText = !isRecording
                      ? 'Record'
                      : canStop
                      ? 'Stop'
                      : 'After 5s you can stop'
                    return (
                      <div
                        className={`vrm-mic-wrap ${(() => {
                          if (isPaused) return 'paused'
                          if (isRecording && micLevel !== 'silent') return 'active'
                          if (isRecording) return 'recording'
                          return ''
                        })()} ${micDisabled ? 'disabled' : ''}`}
                        role='button'
                        tabIndex={micDisabled ? -1 : 0}
                        aria-disabled={micDisabled}
                        title={titleText}
                        onClick={() => {
                          if (isUploading || micDisabled) return
                          if (!isRecording) {
                            startRecording()
                          } else if (canStop) {
                            stopRecording()
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            if (isUploading || micDisabled) return
                            if (!isRecording) {
                              startRecording()
                            } else if (canStop) {
                              stopRecording()
                            }
                          }
                        }}
                      >
                        <i className={`bi ${!isRecording ? 'bi-mic-fill' : 'bi-stop-fill'}`}></i>
                      </div>
                    )
                  })()}
                  <div>
                    <div className='fw-bold fs-5'>Voice Recorder</div>
                    <div className='text-muted small'>Record a short note for this contact</div>
                  </div>
                </div>

                {/* Recording Timer - Top Right Corner */}
                {isRecording && (
                  <div className='recording-timer position-absolute' style={{top: '0', right: '0'}}>
                    <span className='badge bg-light text-dark fs-6 px-2 py-1 border'>
                      {formatTime(recordingTime)}
                    </span>
                  </div>
                )}
              </div>

              {/* Preview */}
              {hasRecordedAudio && recordedAudioUrl && (
                <div className='d-flex align-items-center gap-3 col-12'>
                  <audio controls src={recordedAudioUrl} />
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light-danger'
                    title='Delete'
                    onClick={() => discardRecording()}
                    disabled={isUploading || isRecording}
                  >
                    <i className='bi bi-trash'></i>
                  </button>
                </div>
              )}

              {isUploading && activeTab === 'record' && <PageLoading />}
            </section>
          </div>

          {/* Upload file section */}
            {
              activeTab === 'upload' && (
                <div className='vrm-upload-section mt-4'>
              <input
                ref={fileInputRef}
                type='file'
                accept='audio/*'
                className='d-none'
                onChange={handleFileChange}
              />
              {!uploadedFile ? (
                <div
                  className='vrm-upload-dropzone d-flex flex-column align-items-center justify-content-center gap-3 p-6 rounded border border-dashed border-gray-300'
                  style={{cursor: 'pointer', minHeight: '120px'}}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault()
                    const file = e.dataTransfer.files?.[0]
                    if (!file) return
                    if (uploadedFileUrl) URL.revokeObjectURL(uploadedFileUrl)
                    setUploadedFile(file)
                    setUploadedFileUrl(URL.createObjectURL(file))
                  }}
                >
                  <i className='bi bi-cloud-arrow-up fs-2x text-muted'></i>
                  <div className='text-center'>
                    <div className='fw-bold text-gray-700'>Click to browse or drag & drop</div>
                    <div className='text-muted small'>Supports MP3, WAV, M4A, OGG, and other audio formats</div>
                  </div>
                </div>
              ) : (
                <div className='d-flex align-items-center gap-3'>
                  <audio controls src={uploadedFileUrl ?? undefined} className='flex-grow-1' />
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light-danger'
                    title='Remove'
                    onClick={handleRemoveUploadedFile}
                    disabled={isUploading}
                  >
                    <i className='bi bi-trash'></i>
                  </button>
                </div>
              )}
              {isUploading && <PageLoading />}
            </div>
              )
            }
        </div>
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-end py-6 px-9'>
        <button
          className='btn btn-active-light-primary me-2'
          onClick={() => {
            try {
              forceStopRecording()
            } catch (_) {}
            discardRecording()
            resetRecordingState()
            onClose()
          }}
          disabled={isUploading}
        >
          Cancel
        </button>
        <button
          className='btn btn-primary'
          onClick={async () => {
            try {
              if (activeTab === 'upload') {
                if (!uploadedFile) return
                await uploadFile(uploadedFile)
                handleRemoveUploadedFile()
              } else {
                if (isRecording) {
                  if (!canStop) return
                  stopRecording()
                }
                await Promise.resolve(saveRecording())
              }
              if (onSaved) onSaved()
            } catch (_) {}
          }}
          disabled={isUploading || (activeTab === 'record' ? !hasRecordedAudio : !uploadedFile)}
        >
          {isUploading ? 'Saving...' : 'Save'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default VoiceRecordModal
